import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero, Section } from "@/components/layout/Page";
import { AnimatedAquariumEcosystem } from "@/components/visuals/AnimatedAquariumEcosystem";
import {
  CheckCircle2,
  ArrowRight,
  Store,
  Fish,
  Droplets,
  Building2,
  HeartHandshake,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";
import { QuoteDrawer } from "@/components/layout/QuoteDrawer";
import { SUBMISSION_SOURCES } from "@/lib/submission-context";
import { PRODUCTS } from "@/lib/mock/products";

import heroReef from "@/assets/aquariums/optimized/reef-aquarium-display-1920.webp";
import imgPublic from "@/assets/aquariums/optimized/public-aquarium-diver-reef-exhibit-1024.webp";
import imgShops from "@/assets/aquariums/optimized/aquarium-shop-tank-display-1024.webp";
import imgBreeders from "@/assets/aquariums/optimized/betta-breeder-tank-1024.webp";
import imgReef from "@/assets/aquariums/optimized/coral-reef-aquarium-wall-1024.webp";
import imgGoldfishBanner from "@/assets/aquariums/optimized/goldfish-koi-surface-feeding-1920.webp";
import imgGoldfishFancy from "@/assets/aquariums/optimized/fancy-goldfish-ornamental-1024.webp";
import imgGoldfishHome from "@/assets/aquariums/optimized/home-goldfish-aquarium-1024.webp";
import imgPlanted from "@/assets/aquariums/optimized/planted-community-aquarium-1920.webp";

export const Route = createFileRoute("/aquariums-and-hobbyists")({
  head: () => ({
    meta: getLocalizedMeta("aquariums"),
    scripts: [faqHeadScript("aquariums", "/aquariums-and-hobbyists")],
  }),
  component: AquariumsAndHobbyistsPage,
});

/**
 * Products this audience actually buys, in the order they get asked about.
 *
 * The page used to link to no product at all — it described four buyer segments
 * and then offered them a contact form, which is a dead end for someone who has
 * already decided what they want. These four are the live-feed lines an
 * ornamental breeder, shop or public aquarium uses; the two Artemia slugs are
 * the same pair that `AQUARIUM_TARGETED_SLUGS` on the product pages points back
 * here, so the cross-link now runs both ways.
 */
const RELATED_SLUGS = [
  "artemia",
  "decapsulated-dry-artemia-cysts",
  "super-fresh-chlorella-v12",
  "emerald-chlorella-powder",
] as const;

function AquariumsAndHobbyistsPage() {
  const { t } = useI18n();
  const [quoteFor, setQuoteFor] = useState<{ slug: string; title: string } | null>(null);

  const SECTORS = [
    {
      key: "public",
      icon: Building2,
      image: imgPublic,
      width: 1024,
      height: 683,
    },
    {
      key: "shops",
      icon: Store,
      image: imgShops,
      width: 1024,
      height: 1624,
    },
    {
      key: "breeders",
      icon: Fish,
      image: imgBreeders,
      width: 1024,
      height: 678,
    },
    {
      key: "reef",
      icon: Droplets,
      image: imgReef,
      width: 1024,
      height: 683,
    },
  ] as const;

  const related = RELATED_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug)).filter(
    (p): p is NonNullable<typeof p> => Boolean(p),
  );

  // Per-product override, same scheme as ProductCard: a locale that has
  // translated a product title gets it here too, rather than the English one.
  const tp = (slug: string, field: string, fallback: string) => {
    const key = `product.${slug}.${field}`;
    const res = t(key);
    return res === key ? fallback : res;
  };

  return (
    <>
      <PageHero
        image={heroReef}
        imageWidth={1920}
        imageHeight={996}
        eyebrow={t("hobbyists.hero.eyebrow")}
        title={t("hobbyists.hero.title")}
        description={t("hobbyists.hero.description")}
      />

      <Section>
        <div className="grid md:grid-cols-2 gap-8">
          {SECTORS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className="glass-card rounded-2xl border border-border/80 bg-background shadow-md overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-surface-alt/80">
                  <img
                    src={s.image}
                    alt={t(`hobbyists.sector.${s.key}.imageAlt`)}
                    loading="lazy"
                    decoding="async"
                    width={s.width}
                    height={s.height}
                    className="h-full w-full object-cover"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent"
                    aria-hidden="true"
                  />
                </div>

                <div className="p-8 space-y-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-teal/20 text-navy flex items-center justify-center font-bold shrink-0">
                      <Icon className="h-6 w-6 text-mint-ink" />
                    </div>
                    <h2 className="font-display text-xl font-bold text-navy">
                      {t(`hobbyists.sector.${s.key}.title`)}
                    </h2>
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t(`hobbyists.sector.${s.key}.desc`)}
                  </p>

                  <ul className="space-y-2 pt-2 mt-auto border-t border-border/60">
                    {["b1", "b2", "b3"].map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-xs font-semibold text-navy"
                      >
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>{t(`hobbyists.sector.${s.key}.${b}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Goldfish, koi and ornamental fry.
          The four sector cards above are written for buyers; this band is
          written for the fish. It is also the only place on the site that says
          "goldfish" — the segment copy never did, so nothing here could rank
          for the ornamental queries that bring this audience in. */}
      <Section band>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          {/* Fixed 4:3 rather than the image's native 16:9. At two columns the
              prose runs taller than a 16:9 crop, which left the picture floating
              in the middle of an empty column. */}
          <div className="aspect-[4/3] overflow-hidden rounded-3xl border border-border/80 shadow-md">
            <img
              src={imgGoldfishBanner}
              alt={t("hobbyists.goldfish.imageAlt.banner")}
              loading="lazy"
              decoding="async"
              width={1920}
              height={1080}
              className="h-full w-full object-cover"
            />
          </div>

          <div>
            <div className="mb-4 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" />
              {t("hobbyists.goldfish.eyebrow")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
              {t("hobbyists.goldfish.title")}
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>{t("hobbyists.goldfish.p1")}</p>
              <p>{t("hobbyists.goldfish.p2")}</p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <img
                src={imgGoldfishFancy}
                alt={t("hobbyists.goldfish.imageAlt.fancy")}
                loading="lazy"
                decoding="async"
                width={1024}
                height={768}
                className="aspect-[4/3] w-full rounded-2xl border border-border/80 object-cover shadow-sm"
              />
              <img
                src={imgGoldfishHome}
                alt={t("hobbyists.goldfish.imageAlt.home")}
                loading="lazy"
                decoding="async"
                width={1024}
                height={1536}
                className="aspect-[4/3] w-full rounded-2xl border border-border/80 object-cover shadow-sm"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl text-center space-y-3">
          <h2 className="font-display text-3xl font-bold text-navy">
            {t("hobbyists.products.title")}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("hobbyists.products.lead")}
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((p) => (
            <div
              key={p.slug}
              className="glass-card flex flex-col overflow-hidden rounded-2xl border border-border/80 bg-background shadow-md transition-shadow hover:shadow-xl"
            >
              <div className="flex aspect-[4/3] items-center justify-center bg-surface-alt/80 p-4">
                <img
                  src={p.thumbnail ?? p.image}
                  alt={tp(p.slug, "title", p.title)}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={600}
                  className="h-full w-full object-contain"
                />
              </div>

              <div className="flex flex-1 flex-col gap-3 p-5">
                <h3 className="font-display text-base font-bold text-navy">
                  {tp(p.slug, "title", p.title)}
                </h3>
                {/* Hobbyist framing, not the hatchery tagline: what it does in a
                    tank, in one sentence. Written per product, so it is a key
                    rather than a slice of the catalogue copy. */}
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t(`hobbyists.product.${p.slug}.pitch`)}
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-mint-ink">
                  {t(`hobbyists.product.${p.slug}.spec`)}
                </p>

                <div className="mt-auto space-y-2 pt-2">
                  <Button
                    size="sm"
                    className="w-full rounded-xl font-bold"
                    onClick={() =>
                      setQuoteFor({ slug: p.slug, title: tp(p.slug, "title", p.title) })
                    }
                  >
                    <MessageSquare className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                    {t("hobbyists.products.quote")}
                  </Button>
                  <Link
                    to="/products/$category/$slug"
                    params={{ category: p.category, slug: p.slug }}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    {t("hobbyists.products.spec")}
                    <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <AnimatedAquariumEcosystem />
        </div>
      </Section>

      <Section>
        <div className="relative overflow-hidden rounded-3xl border border-border/80 shadow-md">
          <img
            src={imgPlanted}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            width={1920}
            height={1280}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-navy/80" aria-hidden="true" />

          <div className="relative mx-auto max-w-3xl space-y-4 px-8 py-14 text-center">
            <h2 className="font-display text-2xl font-bold text-white md:text-3xl">
              {t("hobbyists.cta.title")}
            </h2>
            <p className="text-sm leading-relaxed text-white/80">
              {t("hobbyists.cta.description")}
            </p>
            <div className="pt-2">
              <Button
                size="lg"
                asChild
                className="rounded-xl bg-white font-bold text-navy hover:bg-white/90"
              >
                <Link to="/contactus">
                  <HeartHandshake className="mr-2 h-4 w-4" aria-hidden="true" />{" "}
                  {t("hobbyists.cta.button")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <FaqSection page="aquariums" />

      <QuoteDrawer
        open={quoteFor !== null}
        onOpenChange={(open) => !open && setQuoteFor(null)}
        productSlug={quoteFor?.slug}
        productTitle={quoteFor?.title}
        source={SUBMISSION_SOURCES.aquariumsHobbyists}
      />
    </>
  );
}
